// Shared helper for syncing Disclosure Tracking Mapping to Pushback API
import { Dispatch } from "redux";
import { Action, ActionType } from "../actions";
import PluginService from "../../services/pluginService";
import { IDisclosureTrackingMapping } from "../../models/configuration/plugin/IConfiguration";
import { IPushbackConfiguration } from "../../models/configuration/pushback/IConfiguration";
import EncompassService from "../../services/encompassService";

/**
 * Syncs Disclosure Tracking Mapping from Plugin configuration to Pushback Processor configuration.
 *
 * @param dispatch Redux dispatch function
 * @param token Access token
 * @param configId Configuration ID
 * @param disclosureTrackingMapping The mapping data to sync
 * @returns Promise<boolean> - true if sync succeeded, false if it should be retried
 * @throws Error if sync fails and cannot be retried
 */
export const syncDisclosureTrackingToPushback = async (
    dispatch: Dispatch<Action>,
    token: string | null,
    configId: string,
    disclosureTrackingMapping: IDisclosureTrackingMapping
): Promise<boolean> => {
    try {
        // Mark Pushback sync as pending (will be cleared on success)
        dispatch({ type: ActionType.SET_PUSHBACK_SYNC_PENDING });

        // Fetch current Pushback configuration
        const pushbackConfig = await new PluginService().getConfig<IPushbackConfiguration>(
            token,
            "pushback-processor",
            configId
        );

        // Create or update Pushback configuration
        let pushbackConfigData = pushbackConfig?.Configuration;
        if (!pushbackConfigData) {
            // First-time save: create new PushbackProcessorConfiguration
            pushbackConfigData = {
                DisclosureTrackingMapping: disclosureTrackingMapping
            } as any; // Cast to avoid TypeScript issues with partial object
        } else {
            // Update existing configuration
            pushbackConfigData = {
                ...pushbackConfigData,
                DisclosureTrackingMapping: disclosureTrackingMapping
            };
        }

        const pushbackRequestBody = {
            "ConfigurationId": configId,
            "EncompassInstanceId": EncompassService.getEncompassInstanceId(),
            "Configuration": pushbackConfigData
        };

        // Save updated Pushback configuration
        await new PluginService().saveConfig<IPushbackConfiguration>(
            token,
            "pushback-processor",
            configId,
            pushbackRequestBody
        );

        // Success - clear the pending flag
        dispatch({ type: ActionType.CLEAR_PUSHBACK_SYNC_PENDING });

        dispatch({
            type: ActionType.SAVE_CONFIG_PUSHBACK_SUCCESS,
            payload: {
                Configuration: pushbackConfigData
            }
        });

        return true;

    } catch (pushbackError: any) {
        // Keep pushbackSyncPending = true (already set above)
        // User-friendly error message explaining they can retry
        const payloadErrorMessage = `Plugin configuration saved successfully, but Pushback sync failed. Please click Save again to retry the sync. Error: ${pushbackError.message}`;

        dispatch({
            type: ActionType.SAVE_CONFIG_PUSHBACK_FAILURE,
            payload: payloadErrorMessage,
        });

        // Don't throw - Plugin save succeeded
        // Return false to indicate retry is needed
        return false;
    }
};
